// src/features/records/components/FilterButton.js
import React, { useMemo, useState } from 'react';
import { Blend } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AvatarStack from './shared/AvatarStack';
import { Avatar, AvatarSkeleton, AVATAR_VARIANTS } from '../../../components/avatars';
import FilterModal from './FilterModal';
import useFilterStore from '../stores/useFilterStore';
import useUser from '../../../hooks/useUser';
import useUserProfiles from '../hooks/useUserProfiles';
import { useGroups } from '../../groups';
import styles from './FilterButton.module.css';

/**
 * Filter button component that displays current filter state
 * and opens the filter modal
 */
function FilterButton() {
  const { t } = useTranslation();
  const { user } = useUser();
  const scope = useFilterStore((state) => state.scope);
  
  // Compute isFilterActive in the selector to ensure reactivity
  // Gate on initialized to prevent hydration flash
  const isFilterActive = useFilterStore((state) => {
    const { scope, selfId, initialized } = state;
    return initialized && (
      scope.mode === 'group' || 
      (scope.mode === 'users' && (
        scope.userIds?.length !== 1 || 
        scope.userIds[0] !== selfId
      ))
    );
  });
  
  const { groups, loading: groupsLoading } = useGroups();
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Get user profiles for avatar display
  const userIds = useMemo(() => {
    return scope.mode === 'users' ? scope.userIds || [] : [];
  }, [scope.mode, scope.userIds]);
  const { profiles: userProfiles, loading: profilesLoading } = useUserProfiles(userIds);
  
  // Find selected group
  const selectedGroup = useMemo(() => {
    if (scope.mode !== 'group' || !scope.groupId) return null;
    return groups.find(g => g.id === scope.groupId);
  }, [scope, groups]);

  const isUsersLoading = useMemo(() => {
    if (scope.mode !== 'users' || userIds.length === 0) {
      return false;
    }
    if (profilesLoading) {
      return true;
    }
    return userIds.some((id) => !userProfiles[id]);
  }, [scope.mode, userIds, profilesLoading, userProfiles]);

  const isGroupLoading = useMemo(() => {
    if (scope.mode !== 'group' || !scope.groupId) {
      return false;
    }
    return groupsLoading;
  }, [scope.mode, scope.groupId, groupsLoading]);

  const isSkeletonVisible = isUsersLoading || isGroupLoading;
  
  // Prepare display content
  const displayContent = useMemo(() => {
    const selfId = user?.uid;
    
    // Group mode with selection
    if (scope.mode === 'group' && selectedGroup) {
      return {
        type: 'group',
        avatar: {
          name: selectedGroup.name || t('records.filters.group_fallback_name'),
          avatarUrl: selectedGroup.icon
        }
      };
    }
    
    // Users mode
    if (scope.mode === 'users' && userIds.length > 0) {
      // Check if it's default (self only)
      const isSelfOnly = userIds.length === 1 && userIds[0] === selfId;
      
      if (isSelfOnly) {
        return { type: 'default' };
      }
      
      // Multiple users or non-self users
      const users = userIds.map(id => {
        const profile = userProfiles[id];
        return {
          id,
          name: profile?.displayName || (id === selfId
            ? t('records.filters.self_label')
            : t('records.filters.user_fallback_name')),
          avatarUrl: profile?.profilePictureUrl
        };
      });
      
      return {
        type: 'users',
        users
      };
    }
    
    return { type: 'default' };
  }, [scope, selectedGroup, t, user, userIds, userProfiles]);
  
  // Calculate filter count
  const filterCount = useMemo(() => {
    if (scope.mode === 'group' && scope.groupId) {
      return 1;
    }
    if (scope.mode === 'users' && userIds.length > 0) {
      // Don't count if it's just self
      const isSelfOnly = userIds.length === 1 && userIds[0] === user?.uid;
      return isSelfOnly ? 0 : userIds.length;
    }
    return 0;
  }, [scope, userIds, user]);
  
  // Generate tooltip text
  const tooltipText = useMemo(() => {
    if (displayContent.type === 'group') {
      return displayContent.avatar.name;
    }
    
    if (displayContent.type === 'users') {
      const names = displayContent.users.map(u => u.name);
      if (names.length <= 3) {
        return names.join(', ');
      }
      return t('records.filters.tooltip_names_overflow', {
        names: names.slice(0, 3).join(', '),
        count: names.length - 3
      });
    }
    
    return t('records.filters.tooltip_default');
  }, [displayContent, t]);
  
  return (
    <>
      <button
        className={`${styles.filterButton} ${isFilterActive ? styles.active : ''}`}
        onClick={() => setIsModalOpen(true)}
        title={tooltipText}
        aria-label={t('records.filters.open_modal_aria')}
      >
        {!isSkeletonVisible && displayContent.type === 'default' && (
          <div className={styles.defaultContent}>
            <Blend size={20} />
            <span className={styles.label}>{t('records.filters.blend_label')}</span>
          </div>
        )}
        
        {isSkeletonVisible && (
          <div className={styles.avatarContent} aria-hidden="true">
            {isGroupLoading && (
              <>
                <AvatarSkeleton
                  size="sm"
                  className={styles.avatarSkeletonCircle}
                />
                <span className={styles.skeletonLabel} />
              </>
            )}
            {isUsersLoading && (
              <>
                <div className={styles.skeletonAvatarStack}>
                  {(userIds.length > 0 ? userIds : Array.from({ length: 3 }))
                    .slice(0, 3)
                    .map((_, index) => (
                      <AvatarSkeleton
                        key={`skeleton-${index}`}
                        size="xs"
                        className={`${styles.avatarSkeletonCircle} ${styles.skeletonStackCircle}`}
                        style={{ zIndex: 3 - index }}
                      />
                    ))}
                </div>
                {userIds.length === 1 && (
                  <span className={styles.skeletonLabel} />
                )}
              </>
            )}
          </div>
        )}

        {!isSkeletonVisible && displayContent.type === 'group' && (
          <div className={styles.avatarContent}>
            <Avatar
              variant={AVATAR_VARIANTS.GROUP}
              name={displayContent.avatar.name}
              src={displayContent.avatar.avatarUrl}
              size="sm"
              alt={displayContent.avatar.name}
            />
            <span className={styles.label}>{displayContent.avatar.name}</span>
          </div>
        )}
        
        {!isSkeletonVisible && displayContent.type === 'users' && (
          <div className={styles.avatarContent}>
            <AvatarStack 
              users={displayContent.users}
              max={3}
              size="sm"
            />
            {displayContent.users.length === 1 && (
              <span className={styles.label}>{displayContent.users[0].name}</span>
            )}
          </div>
        )}
        
        {filterCount > 0 && (
          <span
            className={styles.filterCount}
            aria-label={t('records.filters.filter_count_badge_aria', { count: filterCount })}
          >
            {filterCount}
          </span>
        )}
      </button>
      
      {isModalOpen && (
        <FilterModal 
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
}

export default FilterButton;
